import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FaImages,
  FaMapMarkedAlt,
  FaCheck,
  FaClock,
  FaTrash,
} from 'react-icons/fa';
import { MY_STUFF_TABS } from '../profileTabs';
import { CachedImage } from '../../../components/common/CachedImage';
import '../profile-design.css';

const PHOTO_FILTERS = [
  { id: 'all', label: 'All Photos' },
  { id: 'harvest', label: 'Hunts' },
  { id: 'message', label: 'Messages' },
  { id: 'profile', label: 'Profile' },
  { id: 'board', label: 'Board' },
];

export function MyStuffSection({
  assets = [],
  assetsScope = 'all',
  onAssetsScopeChange,
  monthlyUsed,
  monthlyLimit,
  onPhotoClick,
  onDeletePhoto,
  onUseAsProfile,
  onLoadMore,
  parcelRequests = [],
  loadingRequests = false,
  onParcelClick,
}) {
  const [tab, setTab] = useState('photos');

  const usagePct =
    monthlyLimit && monthlyUsed != null ? Math.round((monthlyUsed / monthlyLimit) * 100) : 0;
  const uploadsLeft =
    monthlyLimit != null && monthlyUsed != null ? Math.max(0, monthlyLimit - monthlyUsed) : null;

  const filteredAssets =
    assetsScope === 'all' ? assets : assets.filter((a) => a.scope === assetsScope);

  const countForFilter = (filterId) => {
    if (filterId === 'all') return assets.length;
    return assets.filter((a) => a.scope === filterId).length;
  };

  return (
    <div className="y-mystuff" data-testid="my-stuff-section">
      <div className="y-mystuff__tabs" role="tablist" aria-label="My stuff sections">
        {MY_STUFF_TABS.map((t, idx) => {
          const active = tab === t.id;
          const label = t.id === 'photos' ? 'PHOTOS' : 'PARCEL REQUESTS';
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`y-mystuff__tab${active ? ' is-active' : ''}${
                idx === MY_STUFF_TABS.length - 1 ? ' is-last' : ''
              }`}
              onClick={() => setTab(t.id)}
            >
              {label}
            </button>
          );
        })}
      </div>

      {tab === 'photos' ? (
        <div className="y-mystuff__pane">
          <div className="y-mystuff__quota">
            <div className="y-mystuff__quota-copy">
              <div className="y-mystuff__quota-label">MONTHLY UPLOAD ALLOWANCE</div>
              <div className="y-mystuff__quota-value">
                {monthlyUsed != null ? monthlyUsed : '—'}{' '}
                <span className="y-mystuff__quota-of">
                  {monthlyLimit
                    ? `OF ${monthlyLimit} USED`
                    : monthlyUsed != null
                      ? 'UPLOADS THIS MONTH'
                      : 'LOADING…'}
                </span>
              </div>
            </div>
            {monthlyLimit ? (
              <div className="y-mystuff__quota-meter">
                <div className="y-mystuff__quota-meter-head">
                  <span>USAGE</span>
                  <span>{uploadsLeft} LEFT</span>
                </div>
                <div className="y-profile-usage-bar y-profile-usage-bar--tall">
                  <div
                    className={`y-profile-usage-bar__fill${
                      usagePct >= 90 ? ' is-danger' : usagePct >= 70 ? ' is-warning' : ''
                    }`}
                    style={{ width: `${Math.min(100, usagePct)}%` }}
                  />
                </div>
              </div>
            ) : null}
          </div>

          <div className="y-profile-chip-row">
            {PHOTO_FILTERS.map((filter) => {
              const active = assetsScope === filter.id;
              const count = countForFilter(filter.id);
              if (filter.id !== 'all' && count === 0) return null;
              return (
                <button
                  key={filter.id}
                  type="button"
                  className={`y-profile-pill y-profile-pill--soft${active ? ' is-active' : ''}`}
                  aria-pressed={active}
                  onClick={() => onAssetsScopeChange(filter.id)}
                >
                  {filter.label}
                  <span className="y-mystuff__filter-count">{count}</span>
                </button>
              );
            })}
          </div>

          {filteredAssets.length > 0 ? (
            <div className="y-profile-photo-grid">
              {filteredAssets.map((asset) => (
                <div key={asset.id} className="y-profile-photo-card">
                  <button
                    type="button"
                    className="y-profile-photo-card__zoom"
                    onClick={() => onPhotoClick(asset)}
                    aria-label="View photo"
                  >
                    <CachedImage src={asset.thumb_url || asset.url} alt="" loading="lazy" decoding="async" />
                  </button>
                  <div className="y-profile-photo-card__overlay" aria-hidden="true" />
                  <span className="y-profile-photo-card__scope">{asset.scope || 'photo'}</span>
                  <div className="y-profile-photo-card__actions">
                    <button
                      type="button"
                      className="y-mystuff__photo-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUseAsProfile(asset);
                      }}
                    >
                      PROFILE
                    </button>
                    <button
                      type="button"
                      className="y-profile-photo-card__delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeletePhoto(asset);
                      }}
                      aria-label="Delete photo"
                      title="Delete photo"
                    >
                      <FaTrash aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="y-profile-empty">
              <FaImages size={36} aria-hidden="true" />
              <p>No photos in this category yet.</p>
            </div>
          )}

          {filteredAssets.length > 0 ? (
            <button type="button" className="y-mystuff__load-more" onClick={onLoadMore}>
              LOAD MORE
            </button>
          ) : null}
        </div>
      ) : (
        <div className="y-mystuff__pane">
          <section className="y-profile-tip">
            <p className="y-profile-tip__label">PARCEL INFORMATION</p>
            <p className="y-profile-tip__text">
              Drop a pin or draw a boundary on your map to request detailed ownership, acreage, and
              location data pulled from county records.
            </p>
          </section>

          {loadingRequests ? (
            <p className="y-account-empty">Loading…</p>
          ) : parcelRequests.length === 0 ? (
            <div className="y-profile-empty">
              <FaMapMarkedAlt size={36} aria-hidden="true" />
              <p>No parcel requests yet.</p>
            </div>
          ) : (
            <div className="y-mystuff__parcel-list">
              {parcelRequests.map((req) => {
                const ready = req.status === 'succeeded';
                const failed = req.status === 'failed';
                return (
                  <button
                    key={req.id}
                    type="button"
                    className="y-mystuff__parcel-row"
                    onClick={() => onParcelClick(req)}
                  >
                    <span className="y-mystuff__parcel-icon" aria-hidden="true">
                      ◈
                    </span>
                    <span className="y-mystuff__parcel-copy">
                      <span className="y-mystuff__parcel-name">
                        {req.name || req.label || `Request ${req.id}`}
                      </span>
                      <span className="y-mystuff__parcel-meta">
                        {shortLocality(req)} · {new Date(req.created_at).toLocaleDateString()}
                      </span>
                    </span>
                    <span
                      className={`y-profile-status-badge${
                        ready
                          ? ' y-profile-status-badge--ready'
                          : ' y-profile-status-badge--pending'
                      }`}
                    >
                      {ready ? <FaCheck size={10} aria-hidden="true" /> : <FaClock size={10} aria-hidden="true" />}
                      {ready ? 'READY' : failed ? 'FAILED' : 'PROCESSING'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

MyStuffSection.propTypes = {
  assets: PropTypes.array,
  assetsScope: PropTypes.string,
  onAssetsScopeChange: PropTypes.func,
  monthlyUsed: PropTypes.number,
  monthlyLimit: PropTypes.number,
  onPhotoClick: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onUseAsProfile: PropTypes.func,
  onLoadMore: PropTypes.func,
  parcelRequests: PropTypes.array,
  loadingRequests: PropTypes.bool,
  onParcelClick: PropTypes.func,
};

function shortLocality(r) {
  const f = r?.result_fields || r?.tile_properties || {};
  const city = f.scity || f.city || '';
  const state = f.state2 || f.mail_state2 || '';
  if (city || state) return [city, state].filter(Boolean).join(', ');
  const path = f.path || r?.path || '';
  if (path && path.split('/').length >= 3) {
    const parts = path.split('/').filter(Boolean);
    const st = (parts[1] || '').toUpperCase();
    const loc = (parts[2] || '').replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
    return [loc, st].filter(Boolean).join(', ');
  }
  return '—';
}
